interface SortOptions {
  votetype: string; // either support / weighted_support
  default_sort: string;
  commentcount: number;
  new_comments: boolean;
}

// helpers to initialize filter values
import { projectStore } from '@/store/project-store';
import { domainStore } from '@/store/domain-store';
import { computed } from 'vue';
import useConverter from '../general/useConverter';
import { ITopic } from '@/models/topic.model';
export default function useThoughtFilter() {
  const { toBool } = useConverter();

  const project = computed(() => {
    return projectStore.getState().project;
  });

  const voteConfig = computed(() => {
    if (project.value?.config?.show_sort_votecount !== undefined) {
      return toBool(project.value.config.show_sort_votecount);
    } else {
      return true;
    }
  });

  const commentConfig = computed(() => {
    if (project.value?.config?.show_sort_comment !== undefined) {
      return toBool(project.value.config.show_sort_comment);
    } else {
      return true;
    }
  });

  function getVoteRelatedFilterFields(topics: ITopic[], userIsLoggedIn: boolean) {
    const fields: any = {};

    // Any topic that has active voting or has had votes
    const anyTopicHasHadVoting = topics.find((x: any) => x && (x.votetype != 'none' || x.votecount > 0)) != undefined;
    if (anyTopicHasHadVoting) {
      // We have to put a value for default. Empty string won't work
      fields.voted = {
        name: domainStore.translate('discuss.filter.label.voted'),
        type: 'valuelist',
        default_value: 'none',
        values: [
          { id: 'none', ordering: 1, text: domainStore.translate('thought.filter.voted-option.all') },
          { id: '1', ordering: 2, text: domainStore.translate('thought.filter.voted-option.votes') },
          { id: '0', ordering: 3, text: domainStore.translate('thought.filter.voted-option.no-votes') },
        ],
        visible: true,
      };
      if (userIsLoggedIn) {
        fields.user_voted = {
          name: domainStore.translate('discuss.filter.label.user_voted'),
          type: 'valuelist',
          default_value: 'none',
          values: [
            { id: 'none', ordering: 1, text: domainStore.translate('thought.filter.user_voted-option.all') },
            { id: '1', ordering: 2, text: domainStore.translate('thought.filter.user_voted-option.votes') },
            { id: '0', ordering: 3, text: domainStore.translate('thought.filter.user_voted-option.no-votes') },
          ],
          visible: true,
        };
      }
    }
    return fields;
  }

  function getDefaultFilterFields() {
    return {
      text: {
        name: domainStore.translate('discuss.filter.label.text'),
        type: 'text',
        ordering: 1,
        default_value: null,
        visible: true,
      },
      sort: {
        name: domainStore.translate('discuss.filter.label.sort'),
        type: 'valuelist',
        default_value: 'created',
        values: [] as object[],
        visible: true,
        required: true,
      },
    };
  }

  function getWeightOptionsFromTopics(topics: Array<ITopic>) {
    const optionsHash: any = {};
    topics.forEach((t: any) => {
      const weights = t.vote_weight_options;
      if (weights && weights.length > 0) {
        weights.forEach((w: number) => {
          optionsHash[w] = 1;
        });
      }
    });
    return Object.keys(optionsHash).sort();
  }

  function getThoughtWeightField(topics: Array<ITopic>, weightNameMap: any) {
    if (topics.length == 0) {
      return null;
    }
    if (topics.find((x: any) => x.votetype == 'weighted_support') == undefined) {
      return null;
    }
    if (weightNameMap == null || weightNameMap == undefined) {
      weightNameMap = {};
    }
    const voteWeightOptions = getWeightOptionsFromTopics(topics);
    const options = voteWeightOptions.map(opt => {
      let optText = weightNameMap ? weightNameMap[opt] : undefined;
      if (optText == undefined) {
        optText = opt;
      }
      return { id: opt, text: optText, ordering: opt };
    });
    return {
      name: domainStore.translate('discuss.filter.label.vote_weight'),
      type: 'valuelist',
      default_value: 0,
      visible: true,
      filter_name: 'vote_weight',
      values: options,
      allowClear: true,
    };
  }

  function getSortOptions(options: SortOptions) {
    const sortOptions = [];
    let defaultSort = 'random';
    if (options.votetype !== 'none' && voteConfig.value) {
      sortOptions.push({
        id: 'votecount',
        ordering: 1,
        text: domainStore.translate('discuss.filter.sort-option.votecount'),
      });
      if (options.default_sort == 'votecount') {
        defaultSort = options.default_sort;
      }
      if (options.votetype !== 'support' && options.votetype != 'weighted_support') {
        sortOptions.push({
          id: 'votesum',
          ordering: 2,
          text: domainStore.translate('discuss.filter.sort-option.votesum'),
        });
        if (options.default_sort == 'votesum') {
          defaultSort = options.default_sort;
        }
      }
    }

    if (options.votetype == 'weighted_support') {
      // T22744
      sortOptions.push({
        id: 'vote_weight',
        ordering: 3,
        text: domainStore.translate('discuss.filter.sort-option.vote_weight'),
      });
      if (options.default_sort == 'vote_weight') {
        defaultSort = options.default_sort;
      }
      sortOptions.push({
        id: '+vote_weight',
        ordering: 4,
        text: domainStore.translate('discuss.filter.sort-option.+vote_weight'),
      });
      if (options.default_sort == '+vote_weight') {
        defaultSort = options.default_sort;
      }
    }

    if ((options.new_comments || options.commentcount > 0) && commentConfig.value) {
      sortOptions.push({
        id: 'commentcount',
        ordering: 3,
        text: domainStore.translate('discuss.filter.sort-option.commentcount'),
      });
      if (options.default_sort == 'commentcount') {
        defaultSort = options.default_sort;
      }
    }

    sortOptions.push({
      id: 'created',
      ordering: 4,
      text: domainStore.translate('discuss.filter.sort-option.created'),
    });
    if (options.default_sort == 'created') {
      defaultSort = options.default_sort;
    }

    // T21501, sort option for time ascending (oldest to newest)
    sortOptions.push({
      id: '+created',
      ordering: 4,
      text: domainStore.translate('discuss.filter.sort-option.+created'),
    });
    if (options.default_sort == '+created') {
      defaultSort = options.default_sort;
    }

    // T18783: Sort order random
    sortOptions.push({
      id: 'random',
      ordering: 5,
      text: domainStore.translate('discuss.filter.sort-option.random'),
    });
    if (options.default_sort == 'random') {
      defaultSort = options.default_sort;
    }

    return {
      values: sortOptions,
      default: defaultSort,
    };
  }

  return {
    getVoteRelatedFilterFields,
    getDefaultFilterFields,
    getSortOptions,
    getWeightOptionsFromTopics,
    getThoughtWeightField,
  };
}
